import React, { useState } from "react";
import {
  overallPickForRoundPick,
  pickInRoundForTeam,
  teamForPickInRound,
} from "../scenes/draftboard/draftGrid";
import {
  Drawer,
  MenuItem,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  useTheme,
  Menu,
  useMediaQuery,
} from "@mui/material";
import {
  Favorite,
  FavoriteBorder,
  Clear,
  Assignment,
} from "@mui/icons-material";
import { tokens } from "../theme";
import { useSettings } from "../contexts/SettingsContext";

const PlayerContextMenu = ({
  anchorEl,
  open,
  onClose,
  playerData,
  currentRound,
  currentPosition,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    favoritePlayerIds,
    customDraftAssignments,
    toggleFavoritePlayer,
    assignPlayerDraftPosition,
    removePlayerDraftAssignment,
    teamCount,
  } = useSettings();

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignRound, setAssignRound] = useState(currentRound || 1);
  const [assignPosition, setAssignPosition] = useState(
    currentRound && currentPosition
      ? teamForPickInRound(currentRound, currentPosition, teamCount)
      : 1
  );

  if (!playerData) return null;

  const playerId = playerData.sleeper_id || playerData.id;
  const isFavorite = favoritePlayerIds.includes(playerId);
  const hasCustomAssignment = customDraftAssignments[playerId];

  const handleFavoriteToggle = () => {
    toggleFavoritePlayer(playerId);
    onClose();
  };

  const handleAssignPosition = () => {
    setAssignDialogOpen(true);
  };

  const handleConfirmAssignment = () => {
    // console.log(
    //   `🔵 PlayerContextMenu.handleConfirmAssignment: playerId=${playerId}, assignRound=${assignRound}, assignPosition=${assignPosition}`
    // );
    assignPlayerDraftPosition(playerId, assignRound, assignPosition);
    setAssignDialogOpen(false);
    onClose();
  };

  const handleRemoveAssignment = () => {
    removePlayerDraftAssignment(playerId);
    onClose();
  };

  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
  };

  // Shared menu content component
  const MenuContent = ({ isDesktop = false }) => (
    <>
      {/* Player Info Header */}
      {!isDesktop && (
        /* Mobile: Drag handle */
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          pt: 1, 
          pb: 0 
        }}>
          <Box sx={{ 
            width: '36px', 
            height: '4px', 
            backgroundColor: colors.grey[400], 
            borderRadius: '2px' 
          }} />
        </Box>
      )}
      
      <Box
        sx={{
          padding: isDesktop ? "12px 16px" : "8px 16px",
          borderBottom: `1px solid ${colors.grey[700]}`,
          ...(isDesktop && {
            minWidth: '220px',
          }),
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: colors.grey[100],
            fontWeight: "bold",
            fontSize: isDesktop ? "15px" : "14px",
            lineHeight: 1.2,
          }}
        >
          {playerData.name}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: colors.grey[300],
            fontSize: "12px",
            mt: 0.25,
          }}
        >
          {playerData.position} - {playerData.team || "FA"}
        </Typography>
        {currentRound && currentPosition && (
          <Typography
            variant="body2"
            sx={{
              color: colors.grey[300],
              fontSize: "12px",
              mt: 0.25,
            }}
          >
            Draft Position: {currentRound}.{currentPosition} (Overall Pick #{overallPickForRoundPick(currentRound, currentPosition, teamCount)})
          </Typography>
        )}
        {playerData.bye_week !== undefined && (
          <Typography
            variant="body2"
            sx={{
              color: colors.grey[300],
              fontSize: "12px",
              mt: 0.25,
            }}
          >
            Bye Week: {playerData.bye_week}
          </Typography>
        )}
      </Box>

      {/* Menu Items */}
      <MenuItem
        onClick={handleFavoriteToggle}
        sx={{ color: colors.grey[100] }}
      >
        {isFavorite ? (
          <>
            <Favorite sx={{ mr: 1, color: colors.redAccent[500] }} />
            Remove from Favorites
          </>
        ) : (
          <>
            <FavoriteBorder sx={{ mr: 1 }} />
            Add to Favorites
          </>
        )}
      </MenuItem>

      <Divider sx={{ backgroundColor: colors.grey[700] }} />

      <MenuItem
        onClick={handleAssignPosition}
        sx={{ color: colors.grey[100] }}
      >
        <Assignment sx={{ mr: 1 }} />
        {hasCustomAssignment
          ? "Edit Draft Position"
          : "Assign Draft Position"}
      </MenuItem>

      {hasCustomAssignment && (
        <MenuItem
          sx={{
            color: colors.grey[300],
            fontSize: "12px",
            paddingLeft: "32px",
            cursor: "default",
          }}
        >
          Current: Round {hasCustomAssignment.round}, Team{" "}
          {hasCustomAssignment.position}
          <br />
          (Overall Pick #
          {overallPickForRoundPick(
            hasCustomAssignment.round,
            pickInRoundForTeam(
              hasCustomAssignment.round,
              hasCustomAssignment.position,
              teamCount
            ),
            teamCount
          )}
          )
        </MenuItem>
      )}
      {hasCustomAssignment && (
        <MenuItem
          onClick={handleRemoveAssignment}
          sx={{ color: colors.redAccent[500] }}
        >
          <Clear sx={{ mr: 1 }} />
          Remove Assignment
        </MenuItem>
      )}
    </>
  );

  return (
    <>
      {/* Mobile: Bottom Drawer */}
      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={onClose}
          PaperProps={{
            sx: {
              backgroundColor: colors.primary[400],
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              maxHeight: '60vh',
              border: `1px solid ${colors.grey[700]}`,
            },
          }}
          ModalProps={{
            keepMounted: false,
          }}
          style={{ 
            zIndex: 1300 // Above the permanent drawer
          }}
        >
          <MenuContent isDesktop={false} />
        </Drawer>
      ) : (
        /* Desktop: Positioned Menu */
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={onClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              backgroundColor: colors.primary[400],
              border: `1px solid ${colors.grey[700]}`,
              borderRadius: '8px',
              minWidth: '240px',
              maxWidth: '320px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            },
          }}
          MenuListProps={{
            sx: {
              padding: '0',
            },
          }}
          style={{ 
            zIndex: 1300 
          }}
        >
          <MenuContent isDesktop={true} />
        </Menu>
      )}

      {/* Assignment Dialog - rendered separately */}
      {assignDialogOpen && (
        <Dialog
          open={assignDialogOpen}
          onClose={handleCloseAssignDialog}
          PaperProps={{
            sx: {
              backgroundColor: colors.primary[400],
              border: `1px solid ${colors.grey[700]}`,
            },
          }}
        >
          <DialogTitle sx={{ color: colors.grey[100] }}>
            Assign Draft Position for {playerData.name}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <TextField
                label="Round"
                type="number"
                value={assignRound}
                onChange={(e) =>
                  setAssignRound(Math.max(1, parseInt(e.target.value) || 1))
                }
                InputProps={{
                  inputProps: { min: 1, max: 15 },
                  sx: { color: colors.grey[100] },
                }}
                InputLabelProps={{
                  sx: { color: colors.grey[300] },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: colors.grey[700] },
                    "&:hover fieldset": { borderColor: colors.grey[500] },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.primary[200],
                    },
                  },
                }}
              />
              <TextField
                label="Team Position"
                type="number"
                value={assignPosition}
                onChange={(e) =>
                  setAssignPosition(
                    Math.max(
                      1,
                      Math.min(teamCount, parseInt(e.target.value) || 1)
                    )
                  )
                }
                InputProps={{
                  inputProps: { min: 1, max: teamCount },
                  sx: { color: colors.grey[100] },
                }}
                InputLabelProps={{
                  sx: { color: colors.grey[300] },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: colors.grey[700] },
                    "&:hover fieldset": { borderColor: colors.grey[500] },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.primary[200],
                    },
                  },
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: colors.grey[300],
                mt: 2,
                fontSize: "12px",
              }}
            >
              Overall Pick: #{overallPickForRoundPick(
                assignRound,
                pickInRoundForTeam(assignRound, assignPosition, teamCount),
                teamCount
              )}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colors.grey[400],
                mt: 1,
                fontSize: "11px",
              }}
            >
              This player will be locked to this team position when the board is
              resorted. Team position refers to the visual position in the grid:
              Team 1 = leftmost, Team {teamCount} = rightmost. Snake draft order
              is automatically handled by the system.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseAssignDialog}
              sx={{ color: colors.grey[300] }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAssignment}
              sx={{ color: colors.greenAccent[500] }}
            >
              {hasCustomAssignment ? "Update" : "Assign"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default PlayerContextMenu;
